/**
 * How far a running job is (0.95.0): "Queued · waiting for a free worker" with the wait time while its next step
 * has no worker, otherwise a slim progress bar with the step and percent. Used by Monitor, Operations, the
 * Storage tab's transfers and System status → Jobs.
 */
import { Clock } from 'lucide-react';
import { StatusDot } from './Status';

/** How long since `iso`: "12 s", "3 min", "1 h 5 min". */
export function waitedFor(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '';
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}

const STEP_TEXT: Record<string, string> = {
  compose: 'rendering', graphics: 'graphics', subtitles: 'subtitles', finalize: 'finishing', fetching: 'fetching originals', restoring: 'restoring originals', recording: 'waiting for the recording to end',
  proxy: 'proxy', probe: 'inspecting', thumbnail: 'thumbnail', sprite: 'shots and scrub images', waveform: 'waveform', qc: 'quality control', transfer: 'copying',
};

/**
 * The Queued pill with its wait time. Short enough for a table column ("Queued · waiting for worker · 9 s", 0.96.0),
 * the whole sentence in its tooltip, and it may wrap rather than be cut off.
 */
export function QueuedPill({ since }: { since: string | null | undefined }) {
  const wait = waitedFor(since);
  return (
    <StatusDot tone="registered" data-status="waiting" data-testid="job-queued" className="status registered queued-pill"
      title={`Queued · waiting for a free worker${wait ? ` · ${wait}` : ''}. No worker is free for this job's next step yet; it starts when one is.`}>
      {/* It may wrap at the separators, never inside "9 s". */}
      <Clock size={11} /> Queued · waiting{'\u00a0'}for{'\u00a0'}worker{wait ? ` · ${wait.replace(/ /g, '\u00a0')}` : ''}
    </StatusDot>
  );
}

/** A slim progress bar with the step and percent (indeterminate while the percent is unknown). */
export function ProgressLine({ step, percent }: { step?: string | null; percent?: number | null }) {
  const pct = percent == null ? null : Math.max(0, Math.min(100, percent));
  const label = [step ? STEP_TEXT[step] ?? step : null, pct == null ? null : `${Math.round(pct)}%`].filter(Boolean).join(' · ');
  return (
    <div className="job-progress" data-testid="job-progress" data-percent={pct == null ? '' : Math.round(pct)}>
      <div className={`progress ${pct == null ? 'indeterminate' : ''}`} role="progressbar" aria-valuenow={pct ?? undefined} aria-valuemin={0} aria-valuemax={100} aria-label={label || 'In progress'}>
        <span style={{ width: `${pct ?? 0}%` }} />
      </div>
      {label && <span className="faint">{label}</span>}
    </div>
  );
}

/** Queued, or running with a progress line; nothing for a job that is neither. */
export function JobProgress({ active, waiting, waitingSince, step, percent }: { active: boolean; waiting?: boolean; waitingSince?: string | null; step?: string | null; percent?: number | null }) {
  if (!active) return null;
  if (waiting) return <QueuedPill since={waitingSince} />;
  return <ProgressLine step={step} percent={percent} />;
}
